import { ColumnDef } from "@tanstack/react-table"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import DatePicker from "react-datepicker"
import { useTranslation } from "react-i18next"

import { handleToast } from "utils/messages"
import { history } from "utils/routes"

import { getPlants } from "../../../companies/service/api"
import { Company } from "../../../companies/types"
import { compositionGetByCompanyId } from "../../../compositions/service/api"
import { CompositionType } from "../../../compositions/constants"
import { Composition } from "../../../compositions/types"
import { filterCompanyCompositions } from "../../../genericCompositions/utils"
import { MaterialGroup } from "../../../materials/service/api"
import {
  deleteSubmission,
  postSubmission,
  putSubmission,
} from "../../service/api"
import { SubmissionType } from "../../types"
import SubmissionGrid, { GridRow } from "./SubmissionGrid"
import { useGridSubmit } from "./useGridSubmit"

// One UI row -> two backend submissions:
//   recyclate part:  type=CONVERTING (1), is_input=true
//   sorted part:     type=SORTING (2),    is_input=false
//
// We track both server-side ids per UI row so subsequent saves PUT each one.
type SorterRecyclerOutputRow = {
  recyclateId?: number
  sortedId?: number
  companyId?: number
  materialsGroupId?: number
  collectionDate?: Date
  compositionId?: number // Application (optional)
  recyclateEU?: number
  recyclateNonEU?: number
  sortedEU?: number
  sortedNonEU?: number
  administrationNumber?: string
  // For the validate-gating logic that wants `id`.
  id?: number
}

type LocalRow = GridRow<SorterRecyclerOutputRow>

const makeKey = () =>
  `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

const SorterRecyclerOutputGrid: React.FC = () => {
  const { t } = useTranslation()

  const [plants, setPlants] = useState<Company[]>([])
  const plantsRef = useRef<Company[]>([])
  const [compositionsByCompany, setCompositionsByCompany] = useState<
    Record<number, Composition[]>
  >({})
  const compositionsByCompanyRef = useRef<Record<number, Composition[]>>({})
  const [polymersByCompany, setPolymersByCompany] = useState<
    Record<number, MaterialGroup[]>
  >({})

  const [rows, setRows] = useState<LocalRow[]>([{ key: makeKey(), row: {} }])

  // Two payloads built from a single UI row.
  const buildRecyclatePayload = useCallback(
    (row: SorterRecyclerOutputRow) => {
      const netOutputEU = row.recyclateEU ?? 0
      const netOutputNonEU = row.recyclateNonEU ?? 0
      const total = netOutputEU + netOutputNonEU
      return {
        companyId: row.companyId!,
        materialsGroupId: row.materialsGroupId!,
        compositionId: row.compositionId ?? null,
        collectionDate: row.collectionDate!,
        volumeGross: total,
        volumeNet: total,
        netOutputEU,
        netOutputNonEU,
        grossOutputEU: netOutputEU,
        grossOutputNonEU: netOutputNonEU,
        virginAdditives: 0,
        isInput: false,
        administrationNumber: row.administrationNumber ?? "",
        type: SubmissionType.RECYCLING,
      }
    },
    [],
  )

  const buildSortedPayload = useCallback(
    (row: SorterRecyclerOutputRow) => {
      const netOutputEU = row.sortedEU ?? 0
      const netOutputNonEU = row.sortedNonEU ?? 0
      const total = netOutputEU + netOutputNonEU
      return {
        companyId: row.companyId!,
        materialsGroupId: row.materialsGroupId!,
        compositionId: row.compositionId ?? null,
        collectionDate: row.collectionDate!,
        volumeGross: total,
        volumeNet: total,
        netOutputEU,
        netOutputNonEU,
        grossOutputEU: netOutputEU,
        grossOutputNonEU: netOutputNonEU,
        virginAdditives: 0,
        isInput: false,
        administrationNumber: row.administrationNumber ?? "",
        type: SubmissionType.SORTING,
      }
    },
    [],
  )

  // useGridSubmit only handles one create/update pair, so we wrap the two
  // submissions and report the recyclate id back (sorted id is kept in row state).
  const saveOne = useCallback(
    async (
      row: SorterRecyclerOutputRow,
      knownIds: { recyclateId?: number; sortedId?: number },
    ): Promise<
      | { recyclateId: number; sortedId: number }
      | { errors: string[] }
    > => {
      const errors: string[] = []
      let recId = knownIds.recyclateId ?? row.recyclateId
      let sortId = knownIds.sortedId ?? row.sortedId

      const recyclatePayload = buildRecyclatePayload(row) as any
      if (recId != null) {
        const { data, errors: e } = await putSubmission({
          ...recyclatePayload,
          id: recId,
        })
        if (e) errors.push(...e.map((x) => x.message))
        else recId = data?.id ?? recId
      } else {
        const { data, errors: e } = await postSubmission(recyclatePayload)
        if (e) errors.push(...e.map((x) => x.message))
        else recId = data!.id!
      }

      const sortedPayload = buildSortedPayload(row) as any
      if (sortId != null) {
        const { data, errors: e } = await putSubmission({
          ...sortedPayload,
          id: sortId,
        })
        if (e) errors.push(...e.map((x) => x.message))
        else sortId = data?.id ?? sortId
      } else {
        const { data, errors: e } = await postSubmission(sortedPayload)
        if (e) errors.push(...e.map((x) => x.message))
        else sortId = data!.id!
      }

      if (errors.length > 0) return { errors }
      return { recyclateId: recId!, sortedId: sortId! }
    },
    [buildRecyclatePayload, buildSortedPayload],
  )

  // Wrap into the useGridSubmit signature. The hook reports back one server id
  // (recyclate), and we capture the second one via a parallel ref-style map.
  const [sortedIdMap, setSortedIdMap] = useState<Record<string, number>>({})

  const { states, isSubmitting, submitAll, clearRowState } =
    useGridSubmit<SorterRecyclerOutputRow>({
      validate: (row) => {
        if (
          row.companyId == null ||
          row.materialsGroupId == null ||
          row.collectionDate == null
        ) {
          return null
        }
        const anyOutput =
          (row.recyclateEU ?? 0) +
          (row.recyclateNonEU ?? 0) +
          (row.sortedEU ?? 0) +
          (row.sortedNonEU ?? 0)
        if (anyOutput === 0) return null

        const errors: string[] = []
        if ((row.recyclateEU ?? 0) < 0)
          errors.push(t("Recyclate EU must be ≥ 0"))
        if ((row.recyclateNonEU ?? 0) < 0)
          errors.push(t("Recyclate Non-EU must be ≥ 0"))
        if ((row.sortedEU ?? 0) < 0)
          errors.push(t("Sorted EU must be ≥ 0"))
        if ((row.sortedNonEU ?? 0) < 0)
          errors.push(t("Sorted Non-EU must be ≥ 0"))
        return errors
      },
      create: async (row) => {
        const result = await saveOne(row, {
          recyclateId: row.recyclateId,
          sortedId: row.sortedId,
        })
        if ("errors" in result) return { errors: result.errors }
        return { id: result.recyclateId, serverRow: { ...row, ...result } }
      },
      update: async (row) => {
        const result = await saveOne(row, {
          recyclateId: row.recyclateId ?? row.id,
          sortedId: row.sortedId,
        })
        if ("errors" in result) return { errors: result.errors }
        return { id: result.recyclateId, serverRow: { ...row, ...result } }
      },
    })

  useEffect(() => {
    getPlants().then((d) => {
      if (!d.data) return
      const loaded = d.data as Company[]
      plantsRef.current = loaded
      setPlants(loaded)
      if (loaded.length !== 1) return
      const companyId = loaded[0].id
      const polymers = loaded[0].polymers as MaterialGroup[] | undefined
      const materialsGroupId = polymers?.length === 1 ? polymers[0].id : undefined
      setPolymersByCompany({ [companyId]: polymers ?? [] })
      setRows((prev) =>
        prev.map((r) =>
          r.row.companyId == null
            ? { ...r, row: { ...r.row, companyId, ...(materialsGroupId != null && { materialsGroupId }) } }
            : r,
        ),
      )
      compositionGetByCompanyId({
        companyId,
        type: CompositionType.SORTING_OUTPUT,
      } as any).then(({ data }) => {
        if (!data) return
        const compositions = data as Composition[]
        compositionsByCompanyRef.current = { [companyId]: compositions }
        setCompositionsByCompany({ [companyId]: compositions })
        const filtered = filterCompanyCompositions<Composition>(compositions, undefined, true)
        if (filtered.length === 1) {
          setRows((prev) =>
            prev.map((r) =>
              r.row.companyId === companyId && r.row.compositionId == null
                ? { ...r, row: { ...r.row, compositionId: filtered[0].id } }
                : r,
            ),
          )
        }
      })
    })
  }, [])

  const ensureCompanyData = useCallback(
    (companyId: number) => {
      setPolymersByCompany((prev) => {
        if (prev[companyId]) return prev
        const plant = plantsRef.current.find((p) => p.id === companyId)
        const polymers = plant?.polymers as MaterialGroup[] | undefined
        if (!polymers) return prev
        if (polymers.length === 1) {
          setRows((r) =>
            r.map((row) =>
              row.row.companyId === companyId && row.row.materialsGroupId == null
                ? { ...row, row: { ...row.row, materialsGroupId: polymers[0].id } }
                : row,
            ),
          )
        }
        return { ...prev, [companyId]: polymers }
      })
      setCompositionsByCompany((prev) => {
        if (prev[companyId]) return prev
        compositionGetByCompanyId({
          companyId,
          type: CompositionType.SORTING_OUTPUT,
        } as any).then(({ data }) => {
          if (data) {
            const compositions = data as Composition[]
            compositionsByCompanyRef.current = { ...compositionsByCompanyRef.current, [companyId]: compositions }
            setCompositionsByCompany((p) => ({ ...p, [companyId]: compositions }))
            const filtered = filterCompanyCompositions<Composition>(compositions, undefined, true)
            if (filtered.length === 1) {
              setRows((r) =>
                r.map((row) =>
                  row.row.companyId === companyId && row.row.compositionId == null
                    ? { ...row, row: { ...row.row, compositionId: filtered[0].id } }
                    : row,
                ),
              )
            }
          }
        })
        return prev
      })
    },
    [],
  )

  const updateRow = useCallback(
    (key: string, patch: Partial<SorterRecyclerOutputRow>) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key ? { ...r, row: { ...r.row, ...patch } } : r,
        ),
      )
    },
    [],
  )

  const handleAdd = useCallback(() => {
    const single = plantsRef.current.length === 1 ? plantsRef.current[0] : null
    const companyId = single?.id
    const polymers = single?.polymers as MaterialGroup[] | undefined
    const materialsGroupId = polymers?.length === 1 ? polymers[0].id : undefined
    const compositionId = companyId != null
      ? (() => {
          const list = filterCompanyCompositions<Composition>(
            compositionsByCompanyRef.current[companyId] ?? [],
            undefined,
            true,
          )
          return list.length === 1 ? list[0].id : undefined
        })()
      : undefined
    setRows((prev) => [
      ...prev,
      {
        key: makeKey(),
        row: {
          ...(companyId != null && { companyId }),
          ...(materialsGroupId != null && { materialsGroupId }),
          ...(compositionId != null && { compositionId }),
        },
      },
    ])
  }, [])

  const handleRemove = useCallback(
    async (key: string) => {
      const target = rows.find((r) => r.key === key)
      clearRowState(key)
      // Delete both backend rows if they exist.
      const errs: string[] = []
      if (target?.row.recyclateId != null) {
        const { errors } = await deleteSubmission({
          id: target.row.recyclateId,
        })
        if (errors) errs.push(...errors.map((e) => e.message))
      }
      if (target?.row.sortedId != null) {
        const { errors } = await deleteSubmission({ id: target.row.sortedId })
        if (errors) errs.push(...errors.map((e) => e.message))
      }
      if (errs.length > 0) {
        handleToast(errs, "error")
        return
      }
      setSortedIdMap((m) => {
        const { [key]: _, ...rest } = m
        return rest
      })
      setRows((prev) => prev.filter((r) => r.key !== key))
    },
    [clearRowState, rows],
  )

  const handleSubmit = useCallback(async () => {
    const { ok, savedCount, updated } = await submitAll(
      rows.map(({ key, row }) => ({ key, row })),
    )
    if (updated.length > 0) {
      // useGridSubmit returns the recyclate id; sortedId is on `state.serverRow`,
      // which it doesn't surface — so re-read from local state map.
      setRows((prev) =>
        prev.map((r) => {
          const u = updated.find((x) => x.key === r.key)
          if (!u) return r
          const next = { ...r, row: { ...r.row, recyclateId: u.serverId } }
          const sortId = sortedIdMap[r.key]
          if (sortId != null) next.row.sortedId = sortId
          return next
        }),
      )
    }
    if (ok && savedCount === 0) {
      handleToast(
        [t("Nothing to save — fill in the required fields first.") as string],
        "error",
      )
      return
    }
    if (ok) {
      handleToast([t("Submissions saved") as string])
      history.push("/submissions", { tab: "output" })
    } else {
      handleToast(
        [
          t("Some rows could not be saved. Check the highlighted rows.") as string,
        ],
        "error",
      )
    }
  }, [rows, sortedIdMap, submitAll, t])

  const usedTooltip = t("Used in or sold to converters")
  const soldTooltip = t("Sold to recyclers")

  const columns = useMemo<ColumnDef<LocalRow, any>[]>(
    () => [
      {
        id: "company",
        header: () => <span>{t("Plant name")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <select
              value={r.companyId ?? ""}
              onChange={(e) => {
                const companyId = e.target.value
                  ? Number(e.target.value)
                  : undefined
                if (companyId) ensureCompanyData(companyId)
                updateRow(row.original.key, {
                  companyId,
                  materialsGroupId: undefined,
                  compositionId: undefined,
                })
              }}
            >
              <option value="">{t("Select…")}</option>
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )
        },
        size: 170,
      },
      {
        id: "polymer",
        header: () => <span>{t("Polymer")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          const list = r.companyId ? polymersByCompany[r.companyId] ?? [] : []
          return (
            <select
              value={r.materialsGroupId ?? ""}
              disabled={!r.companyId}
              onChange={(e) =>
                updateRow(row.original.key, {
                  materialsGroupId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {list.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )
        },
        size: 130,
      },
      {
        id: "date",
        header: () => <span>{t("Date")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <DatePicker
              selected={r.collectionDate}
              dateFormat="MMM-yy"
              showMonthYearPicker
              showPopperArrow={false}
              onChange={(date: Date) =>
                updateRow(row.original.key, { collectionDate: date })
              }
              className="form-control"
              placeholderText={t("Pick month")}
            />
          )
        },
        size: 120,
      },
      {
        id: "application",
        header: () => <span>{t("Application")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          const list = r.companyId
            ? filterCompanyCompositions<Composition>(
                compositionsByCompany[r.companyId] ?? [],
                undefined,
                true,
              )
            : []
          return (
            <select
              value={r.compositionId ?? ""}
              disabled={!r.companyId}
              onChange={(e) =>
                updateRow(row.original.key, {
                  compositionId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.productName}
                </option>
              ))}
            </select>
          )
        },
        size: 160,
      },
      {
        id: "recyclateEU",
        header: () => (
          <span>
            {t("Recyclate Output (EU+UK)")}
            <span className="srs-grid-label-info" title={usedTooltip}>
              (i)
            </span>
          </span>
        ),
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              step="any"
              placeholder={t("0")}
              value={r.recyclateEU ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  recyclateEU: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 160,
      },
      {
        id: "recyclateNonEU",
        header: () => <span>{t("Recyclate Output (Non-EU)")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              step="any"
              placeholder={t("0")}
              value={r.recyclateNonEU ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  recyclateNonEU: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 160,
      },
      {
        id: "sortedEU",
        header: () => (
          <span>
            {t("Sorted/shredded Output (EU+UK)")}
            <span className="srs-grid-label-info" title={soldTooltip}>
              (i)
            </span>
          </span>
        ),
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              step="any"
              placeholder={t("0")}
              value={r.sortedEU ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  sortedEU: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 180,
      },
      {
        id: "sortedNonEU",
        header: () => <span>{t("Sorted/shredded Output (Non-EU)")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              step="any"
              placeholder={t("0")}
              value={r.sortedNonEU ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  sortedNonEU: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 180,
      },
      {
        id: "extra",
        header: () => <span>{t("Extra information")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="text"
              placeholder={t("Free text")}
              value={r.administrationNumber ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  administrationNumber: e.target.value,
                })
              }
            />
          )
        },
        size: 180,
      },
    ],
    [
      compositionsByCompany,
      ensureCompanyData,
      plants,
      polymersByCompany,
      soldTooltip,
      t,
      updateRow,
      usedTooltip,
    ],
  )

  return (
    <SubmissionGrid<SorterRecyclerOutputRow>
      rows={rows}
      columns={columns}
      states={states}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    />
  )
}

export default SorterRecyclerOutputGrid
